import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface MobileSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Title shown at the top of the sheet */
  title?: string;
  /** Whether the sheet has a drag handle */
  showHandle?: boolean;
  /** Snap point as a fraction of viewport height (0–1). Default: auto */
  maxHeightFraction?: number;
  /** z-index override */
  zIndex?: number;
  /** Whether clicking the backdrop closes the sheet */
  closeOnBackdrop?: boolean;
}

const MobileSheet: React.FC<MobileSheetProps> = ({
  open,
  onClose,
  children,
  title,
  showHandle = true,
  maxHeightFraction = 0.92,
  zIndex = 200,
  closeOnBackdrop = true,
}) => {
  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    },
    [open, onClose]
  );

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="sheet-backdrop"
            className="mobile-sheet-overlay"
            style={{ zIndex }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeOnBackdrop ? onClose : undefined}
          />

          {/* Sheet panel */}
          <motion.div
            key="sheet-panel"
            className="mobile-sheet-panel"
            style={{
              zIndex: zIndex + 1,
              maxHeight: `${maxHeightFraction * 100}dvh`,
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 420, damping: 38, mass: 0.8 }}
          >
            {showHandle && <div className="mobile-sheet-handle" />}

            {title && (
              <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors tap-scale"
                  aria-label="Close"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L11 11M11 1L1 11"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            )}

            <div className="overflow-y-auto">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileSheet;
